import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Check, ChevronDown, Search } from 'lucide-react';
import { AnchoredPopover, AnchoredPopoverContent, AnchoredPopoverTrigger } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { useProviderSlots, type ProviderSlotsResponse } from '@/services/scheduling/use-provider-slots';
import { useReminderProfilesList } from '@/services/scheduling/use-reminder-profiles-list';
import { reminderRules } from '@/services/scheduling/use-prebook-appointment-profile';
import { specialityDisplay } from '@/scheduling/lib/calendar-mapping';
import { ProviderPickerGrid } from '@/scheduling/components/shared/provider-picker-grid';
import { filterProvidersForCurrentUser } from '@/scheduling/lib/provider-self-filter';
import { useAuthStore } from '@/auth/auth-store';
import type { AppointmentFormValues } from '@/types/appointment-form';

interface ProviderSectionProps {
  organizationId: string;
  practiceId: string;
  /** Open the full-screen Provider Selection view (Flutter `searchProvider` step). */
  onOpenSearch?: () => void;
  /**
   * Edit mode locks the provider — Flutter `provider_widget.dart` is
   * mounted with `showDelete: !widget.isEdit` (create_appointment_widget
   * .dart:1006), so the clear × is hidden and the provider can't be
   * changed once the appointment exists, REGARDLESS of status.
   */
  locked?: boolean;
}

export function ProviderSection({ organizationId, practiceId, onOpenSearch, locked = false }: ProviderSectionProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const selectedProviderId = watch('providerId');
  const selectedProviderName = watch('providerName');
  const selectedProviderSpeciality = watch('providerSpeciality');
  const selectedDate = watch('selectedDate');
  const location = watch('location');
  const reminderProfileId = watch('reminderProfileId');
  // The specialty "All" control narrows the cards below in place (Practice's); the search opens the full view (main).
  const [specialtyFilter, setSpecialtyFilter] = useState<string | null>(null);
  const [specialtyMenuOpen, setSpecialtyMenuOpen] = useState(false);
  // The design's inline search (SchedBookingForm `SearchBox`, state 1b): typing narrows the loaded cards by name or
  // specialty, client-side. The full Provider Selection view stays one click away (the section's action link).
  const [query, setQuery] = useState('');

  // Auto-populate the reminder profile from the practice's ReminderList
  // as soon as a provider is picked — Flutter parity with
  // `calendar_screen.dart:2256-2263` (after AppointmentReasonValueSet
  // returns, fires GetReminderProfilesEvent → picks first
  // enableInAppBooking and writes it to appointment.reminderProfile).
  // Profile pick later overrides this via PreBookSelectedAppointmentProfile.
  const reminderProfilesQuery = useReminderProfilesList(
    selectedProviderId ? practiceId : null,
  );
  useEffect(() => {
    if (reminderProfileId) return;
    const list = reminderProfilesQuery.data;
    if (!list?.length) return;
    const def = list.find((r) => r.enableInAppBooking) ?? list[0];
    if (!def?.id) return;
    setValue('reminderProfileId', def.id, { shouldValidate: true });
    setValue('reminderProfileName', def.profileName ?? null);
    setValue('reminderProfileRules', reminderRules(def));
    setValue('reminderProfile', def);
    // Intentionally narrow deps: re-run only when the fetched list changes,
    // not on every reminder edit (would clobber the user's manual override).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminderProfilesQuery.data]);

  // Memoize the params object so its identity doesn't change every render
  // and waits until LocationSelector has auto-picked the first location.
  // The panel grid shows the unfiltered list (max pageSize=5); search lives
  // in the full-screen ProviderSelectionView (Flutter `searchProvider`).
  const locationId = location?.id;
  const queryParams = useMemo(() => {
    if (!practiceId || !organizationId || !selectedDate || !locationId) {
      return null;
    }
    return {
      organizationId,
      practiceId,
      date: selectedDate,
      locationIds: [locationId],
    };
  }, [organizationId, practiceId, selectedDate, locationId]);

  const { data: allProviders = [], isLoading } = useProviderSlots(queryParams);

  // PROD-1024 (F-SCH-132): a doctor/nurse creating an appointment sees only
  // themselves in the provider list; org-admin/staff see the full roster.
  // Mirrors Flutter create_appointment_widget.dart:2202-2210.
  const user = useAuthStore((s) => s.user);
  const providers = useMemo(
    () => filterProvidersForCurrentUser(allProviders, user),
    [allProviders, user],
  );

  // The unique specialties of the loaded providers, for the one "All" control.
  const specialties = useMemo(() => {
    const seen = new Set<string>();
    for (const p of providers) {
      const display = specialityDisplay(p.speciality);
      if (display) seen.add(display);
    }
    return [...seen].sort();
  }, [providers]);

  const filteredProviders = useMemo(() => {
    const bySpecialty = specialtyFilter
      ? providers.filter((p) => specialityDisplay(p.speciality) === specialtyFilter)
      : providers;
    const q = query.trim().toLowerCase();
    if (!q) return bySpecialty;
    return bySpecialty.filter(
      (p) =>
        (p.name ?? '').toLowerCase().includes(q) || (specialityDisplay(p.speciality) ?? '').toLowerCase().includes(q),
    );
  }, [providers, specialtyFilter, query]);

  const selectProvider = (provider: ProviderSlotsResponse) => {
    setValue('providerId', provider.id, { shouldValidate: true });
    setValue('providerName', provider.name);
    setValue('providerSpeciality', specialityDisplay(provider.speciality) ?? null);
    // Capture the SNOMED-shaped speciality Flutter ships on Book —
    // `{ specialitySkillId, code: { code, displayName, system, ... } }`.
    // Backend's gRPC GetSpecialitiesByCodesAsync looks the appointment up
    // by `code.code`, so a hardcoded "general" crashes the next read.
    // Wire payload uses `displayName`, but our `Coding` interface has
    // `display`; some endpoints ship one, some the other — accept both.
    const sCode = provider.speciality?.code as
      | { code?: string; display?: string; displayName?: string }
      | undefined;
    setValue(
      'providerSpecialityCode',
      sCode?.code
        ? {
            specialitySkillId: provider.specialityId,
            code: {
              code: sCode.code,
              displayName: sCode.displayName ?? sCode.display,
              system: 'https://snomed.info/sct',
              description: null,
              shortName: null,
            },
          }
        : null,
    );
  };

  // Field reset order matches pre-migration verbatim
  // (providerId → providerName → providerSpeciality → providerSpecialityCode).
  // ProviderPickerGrid invokes `onClear` as a single function call —
  // setValue order is preserved here. Important because downstream
  // watchers (slot-availability query, reminder-profile auto-pick)
  // observe `providerId` first; resetting in any other order risks
  // a stale intermediate snapshot during the render cycle.
  // Samer review §4 on PR #322.
  const clearProvider = () => {
    setValue('providerId', null);
    setValue('providerName', null);
    setValue('providerSpeciality', null);
    setValue('providerSpecialityCode', null);
  };

  // Build the SelectedProviderInfo for ProviderPickerGrid out of the
  // RHF form values. We prefer `selectedProviderSpeciality` (the
  // already-formatted string Flutter ships through the form) but fall
  // back to looking up the speciality from the providers list — covers
  // the edit-flow case where the form was hydrated from an existing
  // appointment but the providers list hasn't fetched yet.
  const selectedInfo = selectedProviderId
    ? {
        id: selectedProviderId,
        name: selectedProviderName ?? '',
        photo: providers.find((p) => p.id === selectedProviderId)?.photo ?? null,
        speciality:
          selectedProviderSpeciality ??
          specialityDisplay(
            providers.find((p) => p.id === selectedProviderId)?.speciality,
          ) ??
          null,
      }
    : null;

  return (
    <div className="booking-section" data-slot="booking-section">
      <div className="booking-section-head">
        <p className="booking-section-label">Provider</p>
        {!selectedProviderId && !locked && onOpenSearch ? (
          <button
            type="button"
            onClick={onOpenSearch}
            className="booking-link"
          >
            All providers
          </button>
        ) : null}
      </div>

      {/* Search + specialty, one row (the design's SearchBox + specialty button). The search filters the loaded cards in
          place; the specialty is one "All" control that narrows them too. Hidden once a provider is chosen. */}
      {!selectedProviderId && !locked && (
        <div className="booking-provider-filter">
          <div className="booking-provider-search">
            <Search className="booking-provider-search-icon" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search providers"
              aria-label="Search providers"
              className="booking-provider-search-input"
            />
          </div>
          {specialties.length > 1 && (
            <AnchoredPopover open={specialtyMenuOpen} onOpenChange={setSpecialtyMenuOpen}>
              <AnchoredPopoverTrigger
                render={
                  <button
                    type="button"
                    aria-label={`Specialty: ${specialtyFilter ?? 'All'}`}
                    className={cn('booking-provider-specialty', specialtyFilter && 'booking-provider-specialty-on')}
                  />
                }
              >
                <span className="truncate">{specialtyFilter ?? 'All'}</span>
                <ChevronDown className="booking-provider-specialty-chevron" aria-hidden />
              </AnchoredPopoverTrigger>
              <AnchoredPopoverContent align="end" className="booking-specialty-menu">
                <div role="listbox" aria-label="Specialty" className="booking-specialty-list">
                  {[null, ...specialties].map((sp) => {
                    const on = specialtyFilter === sp;
                    return (
                      <button
                        key={sp ?? 'all'}
                        type="button"
                        role="option"
                        aria-selected={on}
                        onClick={() => {
                          setSpecialtyFilter(sp);
                          setSpecialtyMenuOpen(false);
                        }}
                        className="booking-specialty-option"
                      >
                        <span className="truncate">{sp ?? 'All'}</span>
                        {on ? <Check className="booking-specialty-check" aria-hidden /> : null}
                      </button>
                    );
                  })}
                </div>
              </AnchoredPopoverContent>
            </AnchoredPopover>
          )}
        </div>
      )}

      <ProviderPickerGrid
        providers={filteredProviders}
        selected={selectedInfo}
        isLoading={isLoading}
        onSelect={selectProvider}
        onClear={clearProvider}
        hideSearch
        searchValue={query}
        readOnly={locked}
        layout="list"
        noMatchText={`No provider matches “${query.trim()}”.`}
      />
    </div>
  );
}
